import { Modal, FileUpload, Status, Tag, Message } from '@bitcine/cinesend-theme'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import ImportReport from './import_report'
import useStore from '../../hooks/use_store'
import axios from 'axios'
import { AnimatedRefresh } from '../icons'

const ImportForm = ({ isOpen, onClose, cycleID, assetType, importData, queryKey }) => {
  const queryClient = useQueryClient()

  const { updateModel } = useStore()
  const confirmOrDiscard = (status) => {
    updateModel.mutate(
      {
        endpoint: `api/imports`,
        id: importData.id,
        data: { status: status },
      },
      {
        onSuccess: (res) => {
          if (res.data.data.status === 'discarded') {
            queryClient.invalidateQueries(queryKey)
            return
          }
          // The request doesn't update the status, the job does. So we'll update it here and poll
          queryClient.setQueryData(queryKey, (oldData) => {
            return { ...oldData, import: { ...oldData.import, status: 'confirmation_in_progress' } }
          })
        },
        onError: () => {
          queryClient.invalidateQueries(queryKey)
        },
      }
    )
  }

  const importMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('asset_type', assetType)
      formData.append('cycle_id', cycleID)

      const response = await axios.post(`/api/imports`, formData, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('user_token')}`,
        },
      })

      return response.data
    },
    onError: (data) => {
      queryClient.setQueryData(queryKey, (oldData) => {
        return { ...oldData, import: { ...importData, errors: data.response.data.errors } }
      })
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, (oldData) => {
        return { ...oldData, import: data.import }
      })
    },
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header='Upload Import File'
      // slightly hacky but the secondary button doesn't appear if the primary doesn't
      // We'll show both and disable the primary
      // Mostly incase an import gets stuck as pending we need a bailout for the user
      confirmButton={
        importData?.status === 'awaiting_confirmation' ||
        importData?.status === 'pending' ||
        importData?.status === 'confirmation_in_progress'
          ? {
              text: 'Confirm',
              type: 'success',
              icon: updateModel.isLoading ? AnimatedRefresh : 'check_circle',
              disabled:
                updateModel.isLoading ||
                importData?.status === 'pending' ||
                importData?.status === 'confirmation_in_progress',
              onClick: () => {
                confirmOrDiscard('confirmed')
              },
            }
          : null
      }
      secondaryButton={
        importData?.status === 'awaiting_confirmation' ||
        importData?.status === 'pending' ||
        importData?.status === 'confirmation_in_progress'
          ? {
              text: 'Discard',
              type: 'error',
              icon: updateModel.isLoading ? AnimatedRefresh : 'delete',
              disabled: updateModel.isLoading,
              onClick: () => confirmOrDiscard('discarded'),
            }
          : null
      }
    >
      <div className='text-sm import-form'>
        {importData?.status === 'awaiting_confirmation' ? (
          <ImportReport importData={importData} />
        ) : (
          <Status
            pending={
              importMutation.isLoading ||
              importData?.status === 'pending' ||
              importData?.status === 'confirmation_in_progress'
            }
          >
            <FileUpload
              className={'h-40'}
              includeRemoveButton={false}
              upload={{
                message: 'Drop CSV here',
                accept: {
                  'text/csv': ['.csv'],
                },
                icon: 'document_scanner',
                publicURL: null,
                onFileSelected: (file) => {
                  importMutation.mutate(file)
                },
              }}
              button={{
                text: 'Upload',
              }}
            />
          </Status>
        )}
        {importData && (
          <div className='pt-4 space-y-4'>
            {importData?.file_name && importData?.status !== 'awaiting_confirmation' && (
              <div className='text-xs'>
                Last import <b>{importData.file_name}</b> at <b>{importData.updated_at}</b> by {importData.uploaded_by}{' '}
                <Tag label={importData?.status} type='neutral' outline={true} />
              </div>
            )}
            {importData.errors &&
              (Array.isArray(importData.errors) ? (
                importData.errors.map((line, index) => (
                  <Message small error key={index} className='bg-error-200 p-3 rounded-md shadow'>
                    {line.message}
                  </Message>
                ))
              ) : (
                <Message small error className='bg-error-200 p-3 rounded-md shadow'>
                  {importData.errors}
                </Message>
              ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ImportForm
